/* eslint-disable camelcase */
/**
 * This uses https://shadcnui-expansions.typeart.cc/docs/datetime-picker as a base.
 */
import { faCalendar, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { add, addYears, endOfYear, format } from 'date-fns';
import { type Locale, enUS } from 'date-fns/locale';
import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import Select from '@/components/Select';
import { cn } from '@/lib/cn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Popover from '@radix-ui/react-popover';
import { formatDate, StandardDatePattern, standardDatePatterns } from '@squaredup/utilities';
import { buttonVariants } from 'components/button';
import { defaultWrapperClassName } from 'components/forms/input/Input';
import { DayPicker, DayPickerProps } from 'react-day-picker';

// ---------- utils start ----------
/**
 * regular expression to check for valid hour format (01-23)
 */
function isValidHour(value: string) {
    return /^(0[0-9]|1[0-9]|2[0-3])$/u.test(value);
}

/**
 * regular expression to check for valid minute format (00-59)
 */
function isValidMinuteOrSecond(value: string) {
    return /^[0-5][0-9]$/u.test(value);
}

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean };

function getValidNumber(value: string, { max, min = 0, loop = false }: GetValidNumberConfig) {
    let numericValue = parseInt(value, 10);

    if (!Number.isNaN(numericValue)) {
        if (!loop) {
            if (numericValue > max) {
                numericValue = max;
            }
            if (numericValue < min) {
                numericValue = min;
            }
        } else {
            if (numericValue > max) {
                numericValue = min;
            }
            if (numericValue < min) {
                numericValue = max;
            }
        }
        return numericValue.toString().padStart(2, '0');
    }

    return '00';
}

function getValidHour(value: string) {
    if (isValidHour(value)) {
        return value;
    }
    return getValidNumber(value, { max: 23 });
}

function getValidMinuteOrSecond(value: string) {
    if (isValidMinuteOrSecond(value)) {
        return value;
    }
    return getValidNumber(value, { max: 59 });
}

type GetValidArrowNumberConfig = {
    min: number;
    max: number;
    step: number;
};

function getValidArrowNumber(value: string, { min, max, step }: GetValidArrowNumberConfig) {
    let numericValue = parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
        numericValue += step;
        return getValidNumber(String(numericValue), { min, max, loop: true });
    }
    return '00';
}

function getValidArrowHour(value: string, step: number) {
    return getValidArrowNumber(value, { min: 0, max: 23, step });
}

function getValidArrowMinuteOrSecond(value: string, step: number) {
    return getValidArrowNumber(value, { min: 0, max: 59, step });
}

function setMinutes(date: Date, value: string) {
    const minutes = getValidMinuteOrSecond(value);
    date.setMinutes(parseInt(minutes, 10));
    return date;
}

function setSeconds(date: Date, value: string) {
    const seconds = getValidMinuteOrSecond(value);
    date.setSeconds(parseInt(seconds, 10));
    return date;
}

function setHours(date: Date, value: string) {
    const hours = getValidHour(value);
    date.setHours(parseInt(hours, 10));
    return date;
}

type TimePickerType = 'minutes' | 'seconds' | 'hours';

function setDateByType(date: Date, value: string, type: TimePickerType) {
    switch (type) {
        case 'minutes':
            return setMinutes(date, value);
        case 'seconds':
            return setSeconds(date, value);
        case 'hours':
            return setHours(date, value);
        default:
            return date;
    }
}

function getDateByType(date: Date | null, type: TimePickerType) {
    if (!date) {
        return '00';
    }
    switch (type) {
        case 'minutes':
            return getValidMinuteOrSecond(String(date.getMinutes()));
        case 'seconds':
            return getValidMinuteOrSecond(String(date.getSeconds()));
        case 'hours':
            return getValidHour(String(date.getHours()));
        default:
            return '00';
    }
}

function getArrowByType(value: string, step: number, type: TimePickerType) {
    switch (type) {
        case 'minutes':
            return getValidArrowMinuteOrSecond(value, step);
        case 'seconds':
            return getValidArrowMinuteOrSecond(value, step);
        case 'hours':
            return getValidArrowHour(value, step);
        default:
            return '00';
    }
}

function genMonths(locale: Locale) {
    return Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: format(new Date(2021, i), 'MMMM', { locale })
    }));
}

function genYears(minDate: Date, maxDate: Date) {
    const yearRange = Math.floor(maxDate.getFullYear() - minDate.getFullYear()) + 1;
    return Array.from({ length: yearRange }, (_, i) => ({
        value: minDate.getFullYear() + i,
        label: (minDate.getFullYear() + i).toString()
    }));
}

// ---------- utils end ----------

type CalendarProps = DayPickerProps & {
    maxDate: Date;
    minDate: Date;
    container?: HTMLElement | null;
    locale?: Locale;
};

export function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    minDate,
    maxDate,
    container,
    locale = enUS,
    ...props
}: CalendarProps) {
    const MONTHS = useMemo(() => genMonths(locale), [locale]);
    const YEARS = useMemo(() => genYears(minDate, maxDate), [maxDate, minDate]);

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('p-3 text-textPrimary', className)}
            startMonth={minDate}
            locale={locale}
            endMonth={maxDate}
            classNames={{
                months: 'flex flex-col sm:flex-row space-y-4  sm:space-y-0 justify-center',
                month: 'flex flex-col items-center space-y-4',
                month_caption: 'flex justify-center pt-1 relative items-center',
                caption_label: 'text-md font-medium',
                nav: 'space-x-1 flex items-center ',
                button_previous: cn(
                    buttonVariants({ variant: 'tertiary' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50  hover:opacity-100 absolute left-5 top-5 aria-disabled:opacity-20'
                ),
                button_next: cn(
                    buttonVariants({ variant: 'tertiary' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-5 top-5 aria-disabled:opacity-20'
                ),
                month_grid: 'w-full border-collapse',
                weekday: 'font-normal text-textSecondary text-[0.8rem]',
                week: 'border border-outlinePrimary',
                weeks: 'border border-outlinePrimary rounded-tileRadius',
                day: 'border border-outlinePrimary p-[2px] text-center text-md relative focus-within:relative focus-within:z-20',
                day_button:
                    'font-normal px-[10px] py-[6px] aria-selected:opacity-100 rounded-md hover:bg-secondaryButtonBackgroundHover w-full transition-colors duration-200 ease-in-out',
                range_middle:
                    'relative before:absolute before:left-0 before:-translate-y-1/2 before:top-1/2 before:w-full before:h-[20px] before:bg-primaryButtonBackgroundDisabled bg-transparent',
                range_start: 'bg-primaryButtonBackground',
                range_end: 'bg-primaryButtonBackground',
                selected: '*:!bg-primaryButtonBackground !text-white',
                today: 'text-textLink',
                outside: 'day-outside text-textDisabled aria-selected:text-textDisabled ',
                disabled: 'opacity-50',
                hidden: 'invisible',
                ...classNames
            }}
            components={{
                Chevron: (chevronProps) =>
                    chevronProps.orientation === 'left' ? (
                        <FontAwesomeIcon icon={faChevronLeft} className='w-4 h-4' />
                    ) : (
                        <FontAwesomeIcon icon={faChevronRight} className='w-4 h-4' />
                    ),
                MonthCaption: ({ calendarMonth }) => {
                    return (
                        <div className='inline-flex gap-2'>
                            <Select
                                defaultValue={calendarMonth.date.getMonth().toString()}
                                onValueChange={(value) => {
                                    const newDate = new Date(calendarMonth.date);
                                    newDate.setMonth(Number.parseInt(value, 10));
                                    props.onMonthChange?.(newDate);
                                }}
                            >
                                <Select.TriggerButton variant='secondary' className='px-input py-autocompleteMulti'>
                                    <Select.Value />
                                </Select.TriggerButton>
                                <Select.Content container={container} className='z-10'>
                                    {MONTHS.map((month) => (
                                        <Select.Item key={month.value} value={month.value.toString()}>
                                            {month.label}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                            <Select
                                defaultValue={calendarMonth.date.getFullYear().toString()}
                                onValueChange={(value) => {
                                    const newDate = new Date(calendarMonth.date);
                                    newDate.setFullYear(Number.parseInt(value, 10));
                                    props.onMonthChange?.(newDate);
                                }}
                            >
                                <Select.TriggerButton variant='secondary' className='px-input py-autocompleteMulti'>
                                    <Select.Value />
                                </Select.TriggerButton>
                                <Select.Content container={container} className='z-10'>
                                    {YEARS.map((year) => (
                                        <Select.Item key={year.value} value={year.value.toString()}>
                                            {year.label}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        </div>
                    );
                }
            }}
            {...props}
        />
    );
}
Calendar.displayName = 'Calendar';

interface TimePickerInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    picker: TimePickerType;
    date?: Date | null;
    onDateChange?: (date: Date | undefined) => void;
    onRightFocus?: () => void;
    onLeftFocus?: () => void;
}

const TimePickerInput = forwardRef<HTMLInputElement, TimePickerInputProps>(
    (
        {
            className,
            value,
            id,
            name,
            date = new Date(new Date().setHours(0, 0, 0, 0)),
            onDateChange,
            onChange,
            onKeyDown,
            picker,
            onLeftFocus,
            onRightFocus,
            ...props
        },
        ref
    ) => {
        const [flag, setFlag] = useState<boolean>(false);

        /**
         * allow the user to enter the second digit within 2 seconds
         * otherwise start again with entering first digit
         */
        useEffect(() => {
            if (flag) {
                const timer = setTimeout(() => {
                    setFlag(false);
                }, 2000);

                return () => clearTimeout(timer);
            }
        }, [flag]);

        const calculatedValue = useMemo(() => {
            return getDateByType(date, picker);
        }, [date, picker]);

        const calculateNewValue = (key: string) => {
            return !flag ? `0${key}` : calculatedValue.slice(1, 2) + key;
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Tab') {
                return;
            }
            e.preventDefault();
            if (e.key === 'ArrowRight') {
                onRightFocus?.();
            }
            if (e.key === 'ArrowLeft') {
                onLeftFocus?.();
            }
            if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                const step = e.key === 'ArrowUp' ? 1 : -1;
                const newValue = getArrowByType(calculatedValue, step, picker);
                if (flag) {
                    setFlag(false);
                }
                const tempDate = date ? new Date(date) : new Date();
                onDateChange?.(setDateByType(tempDate, newValue, picker));
            }
            if (e.key >= '0' && e.key <= '9') {
                const newValue = calculateNewValue(e.key);
                if (flag) {
                    onRightFocus?.();
                }
                setFlag((prev) => !prev);
                const tempDate = date ? new Date(date) : new Date();
                onDateChange?.(setDateByType(tempDate, newValue, picker));
            }
        };

        return (
            <input
                ref={ref}
                id={id || picker}
                name={name || picker}
                className={cn(
                    defaultWrapperClassName,
                    'focus:bg-accent focus:text-accent-foreground text-center text-base tabular-nums caret-transparent [&::-webkit-inner-spin-button]:appearance-none',
                    className
                )}
                value={value || calculatedValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.preventDefault();
                    onChange?.(e);
                }}
                type='tel'
                inputMode='decimal'
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    onKeyDown?.(e);
                    handleKeyDown(e);
                }}
                {...props}
            />
        );
    }
);

TimePickerInput.displayName = 'TimePickerInput';

interface TimePickerProps {
    date?: Date | null;
    onChange?: (date: Date | undefined) => void;
    /**
     * Determines the smallest unit that is displayed in the datetime picker.
     * Default is 'second'.
     * */
    granularity?: Granularity;
}

interface TimePickerRef {
    minuteRef: HTMLInputElement | null;
    hourRef: HTMLInputElement | null;
    secondRef: HTMLInputElement | null;
}

const TimePicker = forwardRef<TimePickerRef, TimePickerProps>(({ date, onChange, granularity = 'second' }, ref) => {
    const minuteRef = useRef<HTMLInputElement>(null);
    const hourRef = useRef<HTMLInputElement>(null);
    const secondRef = useRef<HTMLInputElement>(null);
    const periodRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            minuteRef: minuteRef.current,
            hourRef: hourRef.current,
            secondRef: secondRef.current,
            periodRef: periodRef.current
        }),
        [minuteRef, hourRef, secondRef]
    );
    return (
        <div className='flex items-center justify-center max-w-full gap-2'>
            <TimePickerInput
                picker='hours'
                date={date}
                id='datetime-picker-hour-input'
                className='flex-1 max-w-[70px]'
                onDateChange={onChange}
                ref={hourRef}
                onRightFocus={() => minuteRef?.current?.focus()}
            />
            {(granularity === 'minute' || granularity === 'second') && (
                <>
                    :
                    <TimePickerInput
                        picker='minutes'
                        date={date}
                        className='flex-1 max-w-[70px]'
                        onDateChange={onChange}
                        ref={minuteRef}
                        onLeftFocus={() => hourRef?.current?.focus()}
                        onRightFocus={() => secondRef?.current?.focus()}
                    />
                </>
            )}
            {granularity === 'second' && (
                <>
                    :
                    <TimePickerInput
                        picker='seconds'
                        date={date}
                        onDateChange={onChange}
                        className='flex-1 max-w-[70px]'
                        ref={secondRef}
                        onLeftFocus={() => minuteRef?.current?.focus()}
                        onRightFocus={() => periodRef?.current?.focus()}
                    />
                </>
            )}
        </div>
    );
});
TimePicker.displayName = 'TimePicker';

type Granularity = 'day' | 'hour' | 'minute' | 'second';

type DateTimePickerProps = {
    value?: Date;
    onChange?: (date: Date | undefined) => void;
    onMonthChange?: (date: Date | undefined) => void;
    disabled?: boolean;
    placeholder?: string;
    minDate?: Date;
    maxDate?: Date;
    /**
     * The displayPattern prop allows you to control the format of the date displayed in the input.
     * Patterns accepted by formatDate and date-fns, i.e. yyyy-MM-dd, dd/MM/yyyy, etc.
     */
    displayPattern?: StandardDatePattern;

    /**
     * The granularity prop allows you to control the smallest unit that is displayed by DateTimePicker.
     * By default, the value is `second` which shows all time inputs.
     **/
    granularity?: Granularity;
    className?: string;
    /**
     * Show the default month and time when popup the calendar. Default is the current Date().
     **/
    defaultPopupValue?: Date;
    container?: HTMLElement | null;
    locale?: Locale;
} & Pick<DayPickerProps, 'weekStartsOn' | 'showWeekNumber' | 'showOutsideDays'>;

type DateTimePickerRef = {
    value?: Date;
} & Omit<HTMLButtonElement, 'value'>;

const DateTimePicker = forwardRef<Partial<DateTimePickerRef>, DateTimePickerProps>(
    (
        {
            locale = enUS,
            defaultPopupValue = new Date(new Date().setHours(0, 0, 0, 0)),
            value,
            onChange,
            onMonthChange,
            minDate = new Date(1970, 0),
            maxDate = endOfYear(addYears(new Date(), 10)),
            disabled = false,
            granularity = 'second',
            placeholder = 'Pick a date',
            displayPattern = standardDatePatterns.short,
            className,
            container,
            ...props
        },
        ref
    ) => {
        const [month, setMonth] = useState<Date>(value ?? defaultPopupValue);
        const buttonRef = useRef<HTMLButtonElement>(null);
        const [displayDate, setDisplayDate] = useState<Date | undefined>(value ?? undefined);

        /**
         * Makes sure display date updates when value change on
         * parent component
         */
        useEffect(() => {
            setDisplayDate(value);
        }, [value]);

        /**
         * carry over the current time when a user clicks a new day
         * instead of resetting to 00:00
         */
        const handleMonthChange = (newDay: Date | undefined) => {
            if (!newDay) {
                return;
            }
            if (!defaultPopupValue) {
                newDay.setHours(month?.getHours() ?? 0, month?.getMinutes() ?? 0, month?.getSeconds() ?? 0);
                onMonthChange?.(newDay);
                setMonth(newDay);
                return;
            }
            const diff = newDay.getTime() - defaultPopupValue.getTime();
            const diffInDays = diff / (1000 * 60 * 60 * 24);
            const newDateFull = add(defaultPopupValue, { days: Math.ceil(diffInDays) });
            newDateFull.setHours(month?.getHours() ?? 0, month?.getMinutes() ?? 0, month?.getSeconds() ?? 0);
            onMonthChange?.(newDateFull);
            setMonth(newDateFull);
        };

        const onSelect = (newDay?: Date) => {
            if (!newDay) {
                onChange?.(undefined);
                setMonth(defaultPopupValue);
                setDisplayDate(undefined);
                return;
            }
            onChange?.(newDay);
            setMonth(newDay);
            setDisplayDate(newDay);
        };

        useImperativeHandle(
            ref,
            () => ({
                ...buttonRef.current,
                value: displayDate
            }),
            [displayDate]
        );

        return (
            <Popover.Root
                onOpenChange={() => {
                    // reset month displayed, when the calendar is opened.
                    setMonth(value ?? defaultPopupValue);
                }}
            >
                <Popover.Trigger asChild disabled={disabled} aria-label='Date'>
                    <Button
                        variant='input'
                        className={cn(
                            'w-full justify-start text-left font-normal',
                            !displayDate && 'text-muted-foreground',
                            className
                        )}
                        ref={buttonRef}
                    >
                        <FontAwesomeIcon icon={faCalendar} className='w-4 h-4 mr-1' />
                        {displayDate ? (
                            formatDate(displayDate, {
                                pattern: displayPattern
                            })
                        ) : (
                            <span>{placeholder}</span>
                        )}
                    </Button>
                </Popover.Trigger>
                <Popover.Portal container={container}>
                    <Popover.Content
                        className='px-1 border bg-componentBackgroundPrimary text-textPrimary border-outlinePrimary w-[324px] z-10'
                        sideOffset={5}
                    >
                        <Calendar
                            mode='single'
                            selected={displayDate}
                            month={month}
                            container={container}
                            onSelect={(newDate) => {
                                if (newDate) {
                                    newDate.setHours(
                                        month?.getHours() ?? 0,
                                        month?.getMinutes() ?? 0,
                                        month?.getSeconds() ?? 0
                                    );
                                    onSelect(newDate);
                                }
                            }}
                            onMonthChange={handleMonthChange}
                            minDate={minDate}
                            maxDate={maxDate}
                            locale={locale}
                            {...props}
                        />
                        {granularity !== 'day' && (
                            <div className='flex items-center justify-center p-4 border-t gap-x-4 border-outlinePrimary'>
                                <TimePicker
                                    onChange={(dateValue) => {
                                        onChange?.(dateValue);
                                        setDisplayDate(dateValue);
                                        if (dateValue) {
                                            setMonth(dateValue);
                                        }
                                    }}
                                    date={value}
                                    granularity={granularity}
                                />
                            </div>
                        )}
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        );
    }
);

DateTimePicker.displayName = 'DateTimePicker';

export { DateTimePicker, TimePicker, TimePickerInput };
export type { DateTimePickerProps, DateTimePickerRef, TimePickerType };
